import { callServer } from './server.ts';

// Calls to the Open Banking routes of the sync function (BANKING.md §2). The owner signs in at
// their bank, never in this app; the tokens the bank issues stay on the server.

const call = <T = unknown>(method: 'GET' | 'POST', householdId: string, path: string, body?: unknown, query: Record<string, string> = {}) =>
  callServer<T>(`/banking${path}`, { method, query: { householdId, ...query }, body, unreachable: 'BANK_UNAVAILABLE' });

/** Where the owner connects and renews banks shared through Lunch Flow (opened in a new tab). */
export const LUNCHFLOW_URL = 'https://www.lunchflow.app';

export type Institution = { id: string; name: string; country: string; provider: 'FAKE_BANK' | 'LUNCHFLOW' };

export type AccountChoice = {
  externalId: string;
  action: 'ADD' | 'LINK' | 'SKIP';
  accountId: string | null;
  owners: string[];
  name: string | null;
  /** For ADD: the kind of account, when the provider cannot say. */
  type?: 'CURRENT' | 'SAVINGS' | 'CREDIT_CARD' | null;
};

export const bankingApi = {
  institutions: async (householdId: string, country: string) => (await call<{ institutions: Institution[] }>('GET', householdId, '/institutions', undefined, { country })).institutions,
  /** Returns the bank's consent page; the app then leaves for it and comes back to `returnUrl`. */
  connect: async (householdId: string, body: { provider: Institution['provider']; institutionId: string; country: string; returnUrl: string; connectionId: string | null }) =>
    (await call<{ authorizationUrl: string }>('POST', householdId, '/connect', body)).authorizationUrl,
  chooseAccounts: (householdId: string, connectionId: string, choices: AccountChoice[]) => call('POST', householdId, `/${connectionId}/accounts`, { choices }),
  refresh: (householdId: string, connectionId: string) => call('POST', householdId, `/${connectionId}/refresh`),
  disconnect: (householdId: string, connectionId: string) => call('POST', householdId, `/${connectionId}/disconnect`),
  /** The owner renewed the bank's consent in Lunch Flow: its 90 days count again from today. */
  renewed: (householdId: string, connectionId: string) => call<{ consentExpiresAt: string | null }>('POST', householdId, `/${connectionId}/renewed`),
  /** What an account from the bank is: the server re-reads its entries as that kind. */
  setAccountType: (householdId: string, accountId: string, type: 'CURRENT' | 'SAVINGS' | 'CREDIT_CARD') =>
    call<{ changed: number; paired: number }>('POST', householdId, '/account-type', { accountId, type }),
};

/** Where the bank sends the owner back: this app, on the accounts page (optionally for a person). */
export function bankReturnUrl(personId: string | null): string {
  return `${location.origin}${location.pathname}#/accounts${personId ? `?bankPerson=${personId}` : ''}`;
}
